import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ShieldCheck } from "@phosphor-icons/react";

const ATTEST = [
  ["attest_not_rep", "I certify that I am not this participant's spouse, Power of Attorney, legal guardian, or representative payee."],
  ["attest_notify", "I understand that I am not eligible to provide paid CHC waiver services to this participant while holding any of these roles."],
  ["attest_changes", "I must notify Silver Home Care immediately if this information changes and before providing another shift."],
];

export function DesignatedCaregiverCard({ uid, emp, onSaved, startOpen = false }) {
  const rec = [...(emp.employment_records || [])].reverse().find((r) => r.kind === "designated-caregiver");
  const [open, setOpen] = useState(startOpen);
  const [isDes, setIsDes] = useState(null);
  const [clientName, setClientName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [checks, setChecks] = useState({});
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);

  const designated = emp.profile?.employee_class === "designated_dcw";

  const submit = async () => {
    if (isDes === null) { toast.error("Answer the question first"); return; }
    if (isDes && (!clientName.trim() || !relationship.trim())) { toast.error("Client name and relationship are required"); return; }
    if (isDes && !ATTEST.every(([k]) => checks[k])) { toast.error("All three attestations are required"); return; }
    if (signature.trim().length < 3) { toast.error("Type your full legal name to sign"); return; }
    setBusy(true);
    try {
      await api.post(`/employees/${uid}/designated-caregiver`, {
        is_designated: isDes, client_name: clientName.trim(), relationship: relationship.trim(),
        ...checks, signature: signature.trim(),
      });
      toast.success(isDes ? "Recorded — employee class is now Designated DCW" : "Recorded — not a designated caregiver");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="mb-4 rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="designated-caregiver-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-heading text-sm font-semibold text-stone-900">
            <ShieldCheck size={16} className="text-primary" /> Designated caregiver status
            {designated && <span className="rounded-full border border-[#6a3e97]/40 bg-[#6a3e97]/10 px-2 py-0.5 text-[10px] font-bold text-[#6a3e97]" data-testid="designated-dcw-tag">DESIGNATED DCW</span>}
          </p>
          {rec ? (
            <p className="mt-1 text-xs text-stone-500" data-testid="designated-status">
              {rec.data?.is_designated
                ? <>Designated caregiver for <strong>{rec.data.client_name}</strong> ({rec.data.relationship}) — attested {rec.at?.slice(0, 10)} by {rec.data.signature}.</>
                : <>Answered NO — not hired for one specific known client ({rec.at?.slice(0, 10)}).</>}
            </p>
          ) : (
            <p className="mt-1 text-xs text-stone-500">Not answered yet — identifies employees hired to care for one specific person known to them.{" "}
              <a href="/designated-caregiver" className="font-medium text-[#4A235A] underline" data-testid="dc-learn-more">What is a designated caregiver?</a>
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" className="border-stone-300 text-xs" onClick={() => setOpen((v) => !v)} data-testid="designated-toggle-btn">
          {open ? "Close" : rec ? "Re-answer" : "Answer"}
        </Button>
      </div>

      {open && (
        <div className="mt-4 space-y-3 border-t border-stone-100 pt-4" data-testid="designated-form">
          <p className="text-xs leading-relaxed text-stone-500">
            Some caregivers are hired to care for one specific person they already know — often a family member, friend, or
            neighbor — through Pennsylvania's <strong>Community HealthChoices</strong> program. This is a{" "}
            <strong>designated caregiver</strong> arrangement, and it can turn care you're already giving a loved one into
            paid work.{" "}
            <a href="/designated-caregiver" className="font-medium text-[#4A235A] underline" data-testid="dc-learn-more-form">Read how it works</a>.
            The one legal rule: the person providing the care cannot also be the person legally in charge of the client's
            decisions.
          </p>
          <p className="text-sm font-medium text-stone-800">Were you hired by Silver Home Care to provide care for one specific client who is personally known to you?</p>
          <div className="flex gap-2">
            {[["yes", "Yes"], ["no", "No"]].map(([v, label]) => (
              <button key={v} onClick={() => setIsDes(v === "yes")}
                className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${(isDes === (v === "yes")) ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 bg-white text-stone-600 hover:bg-stone-50"}`}
                data-testid={`designated-answer-${v}`}>{label}</button>
            ))}
          </div>
          {isDes && (
            <>
              <div className="grid gap-2 sm:grid-cols-2">
                <input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Client's full name"
                  className="rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="designated-client-name" />
                <input value={relationship} onChange={(e) => setRelationship(e.target.value)} placeholder="Relationship (e.g. daughter, neighbor)"
                  className="rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="designated-relationship" />
              </div>
              <div className="space-y-2">
                {ATTEST.map(([k, text]) => (
                  <label key={k} className="flex items-start gap-2 text-xs leading-relaxed text-stone-700">
                    <input type="checkbox" checked={!!checks[k]} onChange={(e) => setChecks((c) => ({ ...c, [k]: e.target.checked }))}
                      className="mt-0.5 accent-[#6a3e97]" data-testid={`designated-${k}`} />
                    {text}
                  </label>
                ))}
              </div>
              <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs leading-relaxed text-stone-600" data-testid="designated-consequences">
                Your signature is filed on your employee record. While any of these roles applies, you are not eligible to provide paid CHC waiver services to this participant — if this information changes, you must notify the office immediately, before providing another shift. A false statement is grounds for termination.
              </div>
            </>
          )}
          {isDes !== null && (
            <>
              <input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Sign — type your full legal name"
                className="w-full rounded-lg border border-stone-300 p-2.5 text-sm" data-testid="designated-signature" />
              <Button onClick={submit} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="designated-submit">
                {busy ? "Saving…" : "Sign & save"}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
